import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { ArrowLeft, Download, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function DeploymentDetail() {
  const { id } = useParams();
  const [dep, setDep] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    const load = () => api.get(`/oss/deployments/${id}`).then((r) => {
      setDep(r.data);
      if (["ready", "failed"].includes(r.data.status) && pollRef.current) clearInterval(pollRef.current);
    });
    load();
    pollRef.current = setInterval(load, 3000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [id]);

  if (!dep) return <div className="adr-skeleton h-96" />;

  const download = async () => {
    try {
      const token = localStorage.getItem("adriai_token");
      const r = await fetch(`${API}/oss/deployments/${id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u;
      a.download = dep.package_file;
      a.click();
      URL.revokeObjectURL(u);
    } catch {
      toast.error("Download fallito");
    }
  };

  const Icon = dep.status === "ready" ? CheckCircle2 : dep.status === "failed" ? XCircle : Loader2;
  const color = dep.status === "ready" ? "#15803d" : dep.status === "failed" ? "#C8102E" : "#0F3D6B";

  return (
    <div className="max-w-3xl">
      <Link to="/soluzioni" className="inline-flex items-center gap-1.5 text-sm text-[#555555] hover:text-[#0F3D6B] mb-6">
        <ArrowLeft className="w-4 h-4" /> Torna al catalogo
      </Link>
      <div className="adr-overline mb-3">Deployment</div>
      <h1 className="serif text-3xl font-semibold tracking-tight mb-2">{dep.project_name}</h1>
      <p className="text-sm text-[#555555] mb-8">Avviato il {new Date(dep.created_at).toLocaleString("it-IT")}</p>

      <div className="adr-card p-8 text-center">
        <Icon className={`w-12 h-12 mx-auto mb-3 ${dep.status === "running" || dep.status === "queued" ? "animate-spin" : ""}`} style={{ color }} />
        <h2 className="serif text-xl font-semibold mb-2">
          {dep.status === "queued" && "Pacchetto in coda..."}
          {dep.status === "running" && "Generazione in corso..."}
          {dep.status === "ready" && "Pacchetto pronto!"}
          {dep.status === "failed" && "Errore nella generazione"}
        </h2>
        {dep.status === "running" && <p className="text-sm text-[#555555] mb-4">L'agente sta personalizzando docker-compose, .env e la documentazione italiana. Pochi minuti.</p>}
        {dep.status === "failed" && <p className="text-sm text-[#C8102E] mb-4">{dep.error_message}</p>}
        {dep.status === "ready" && (
          <>
            <p className="text-sm text-[#555555] mb-6">L'archivio contiene: docker-compose.yml, .env.example, LEGGIMI.md, INSTALLAZIONE.md, PRIMI_PASSI.md, SUPPORTO.md.</p>
            <button onClick={download} className="adr-btn-primary" data-testid="deployment-download">
              <Download className="w-4 h-4 inline mr-2" /> Scarica il pacchetto
            </button>
          </>
        )}
      </div>

      {dep.status === "ready" && (
        <div className="adr-card p-6 mt-4">
          <div className="adr-overline mb-3">Prossimi passi</div>
          <ol className="space-y-2 text-sm">
            <li>1. Scarica e decomprimi il pacchetto su un server con Docker installato.</li>
            <li>2. Apri <code className="bg-[#F2F5F8] px-1 rounded">INSTALLAZIONE.md</code> e segui i passi.</li>
            <li>3. Fai revisionare la configurazione a un tecnico prima di andare in produzione con dati reali.</li>
          </ol>
        </div>
      )}
    </div>
  );
}
